import AttachFileIcon from "@mui/icons-material/AttachFile";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import ComputerOutlinedIcon from "@mui/icons-material/ComputerOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import WarehouseOutlinedIcon from "@mui/icons-material/WarehouseOutlined";
import { createElement, type ReactNode } from "react";

export interface UserOption {
  id: string;
  label: string;
  subLabel?: string;
}

// These constants mirror the backend enums — update both when the backend changes.
// Keep these option lists in sync with the backend enum *names* (string values).
export const IT_PROJECT_TYPE_OPTIONS = [
  "SiteRefresh",
  "MultiSiteRefresh",
  "RackDeployment",
  "EndUserRefresh",
  "Other",
] as const;

export const IT_PROJECT_STATUS_OPTIONS = [
  "Planning",
  "InProgress",
  "OnHold",
  "Canceled",
  "Closed",
] as const;

export type ItProjectStatusValue = (typeof IT_PROJECT_STATUS_OPTIONS)[number];
export type ItProjectTypeValue = (typeof IT_PROJECT_TYPE_OPTIONS)[number];

export const IT_PROJECT_DEFAULT_DETAIL_TAB_ID = "orders";

export const IT_PROJECT_TAB_HEADER_ACTION_IDS = {
  linkVariants: "linkVariants",
  linkOrders: "linkOrders",
  linkQuotes: "linkQuotes",
  linkServiceEngagements: "linkServiceEngagements",
  uploadAttachment: "uploadAttachment",
} as const;

export type ItProjectTabHeaderActionId =
  (typeof IT_PROJECT_TAB_HEADER_ACTION_IDS)[keyof typeof IT_PROJECT_TAB_HEADER_ACTION_IDS];

export type ItProjectTabCountKey =
  | "orders"
  | "integrationStock"
  | "integrationOrders"
  | "quotes"
  | "services"
  | "attachments"
  | "shipments";

export interface ItProjectTabConfig {
  id: string;
  label: string;
  friendlyDescription: string;
  icon: ReactNode;
  emptyTitle: string;
  emptyDescription: string;
  countKey?: ItProjectTabCountKey;
  headerAction?: {
    id: ItProjectTabHeaderActionId;
    label: string;
    ariaLabel: string;
    telemetryName: string;
  };
}

export const IT_PROJECT_STATUS_CHIP_COLORS: Record<
  string,
  "warning" | "success" | "info" | "error" | "default"
> = {
  Planning: "info",
  InProgress: "warning",
  OnHold: "default",
  Canceled: "error",
  Closed: "success",
};

export const IT_PROJECT_TYPE_CHIP_COLORS: Record<
  string,
  "primary" | "secondary" | "warning" | "success" | "default"
> = {
  SiteRefresh: "primary",
  EndUserRefresh: "secondary",
  RackDeployment: "warning",
  MultiSiteRefresh: "success",
  Other: "default",
};

const IT_PROJECT_TYPE_LABELS: Record<string, string> = {
  SiteRefresh: "Site Refresh",
  MultiSiteRefresh: "Multi-Site Refresh",
  RackDeployment: "Rack Deployment",
  EndUserRefresh: "End User Refresh",
  Other: "Other (specify below)",
};

const IT_PROJECT_STATUS_LABELS: Record<string, string> = {
  Planning: "Planning",
  InProgress: "In Progress",
  OnHold: "On Hold",
  Canceled: "Canceled",
  Closed: "Closed",
};

export function getItProjectTypeLabel(projectType: string): string {
  return IT_PROJECT_TYPE_LABELS[projectType] ?? projectType;
}

export function getItProjectStatusLabel(status: string): string {
  return IT_PROJECT_STATUS_LABELS[status] ?? status;
}

export const IT_PROJECTS_TAB_CONFIGS: ItProjectTabConfig[] = [
  {
    id: "orders",
    label: "Orders",
    friendlyDescription: "Sales orders linked to this project",
    icon: createElement(WarehouseOutlinedIcon),
    emptyTitle: "No orders linked",
    emptyDescription:
      "Link orders to view sales orders associated with this project.",
    countKey: "orders",
    headerAction: {
      id: IT_PROJECT_TAB_HEADER_ACTION_IDS.linkOrders,
      label: "Link Orders",
      ariaLabel: "Link Orders",
      telemetryName: "it_project_link_orders",
    },
  },
  {
    id: "quotes",
    label: "Quotes",
    friendlyDescription: "Quotes associated with this project",
    icon: createElement(DescriptionOutlinedIcon),
    emptyTitle: "No quotes linked",
    emptyDescription:
      "Link quotes to associate pricing and proposals with this project.",
    countKey: "quotes",
    headerAction: {
      id: IT_PROJECT_TAB_HEADER_ACTION_IDS.linkQuotes,
      label: "Link Quotes",
      ariaLabel: "Link Quotes",
      telemetryName: "it_project_link_quotes",
    },
  },
  {
    id: "integration-stock",
    label: "Integration Stock",
    friendlyDescription: "Inventory stock items linked via variant",
    icon: createElement(Inventory2OutlinedIcon),
    emptyTitle: "No integration stock linked",
    emptyDescription:
      "Link integration variants to manage warehouse inventory for this project.",
    countKey: "integrationStock",
    headerAction: {
      id: IT_PROJECT_TAB_HEADER_ACTION_IDS.linkVariants,
      label: "Link Integration Variants",
      ariaLabel: "Link Integration Variants",
      telemetryName: "it_project_link_variants",
    },
  },
  {
    id: "integration-orders",
    label: "Integration Orders",
    friendlyDescription: "Warehouse orders linked via variant",
    icon: createElement(WarehouseOutlinedIcon),
    emptyTitle: "No integration orders linked",
    emptyDescription:
      "Link integration variants to track warehouse orders for this project.",
    countKey: "integrationOrders",
    headerAction: {
      id: IT_PROJECT_TAB_HEADER_ACTION_IDS.linkVariants,
      label: "Link Integration Variants",
      ariaLabel: "Link Integration Variants",
      telemetryName: "it_project_link_variants",
    },
  },
  {
    id: "shipments",
    label: "Shipments",
    friendlyDescription: "Shipment groups and tracking from linked orders",
    icon: createElement(LocalShippingIcon),
    emptyTitle: "No shipments yet",
    emptyDescription:
      "Shipments will appear automatically when linked orders are fulfilled and shipped.",
    countKey: "shipments",
    headerAction: {
      id: IT_PROJECT_TAB_HEADER_ACTION_IDS.linkOrders,
      label: "Link Orders",
      ariaLabel: "Link Orders",
      telemetryName: "it_project_link_orders",
    },
  },
  {
    id: "assets",
    label: "Assets",
    friendlyDescription: "Purchased assets from linked orders",
    icon: createElement(ComputerOutlinedIcon),
    emptyTitle: "No assets linked",
    emptyDescription:
      "Assets will appear automatically once orders are delivered and assigned to this project.",
    headerAction: {
      id: IT_PROJECT_TAB_HEADER_ACTION_IDS.linkOrders,
      label: "Link Orders",
      ariaLabel: "Link Orders",
      telemetryName: "it_project_link_orders",
    },
  },
  {
    id: "services",
    label: "Services",
    friendlyDescription: "Service engagements for this project",
    icon: createElement(BuildOutlinedIcon),
    emptyTitle: "No service engagements linked",
    emptyDescription:
      "Link service engagements to track project work, timelines, and assigned leads.",
    countKey: "services",
    headerAction: {
      id: IT_PROJECT_TAB_HEADER_ACTION_IDS.linkServiceEngagements,
      label: "Link Service Engagements",
      ariaLabel: "Link Service Engagements",
      telemetryName: "it_project_link_services",
    },
  },
  {
    id: "attachments",
    label: "Attachments",
    friendlyDescription: "Files and web links attached to this project",
    icon: createElement(AttachFileIcon),
    emptyTitle: "No attachments",
    emptyDescription:
      "Upload files to share project documents, specs, and supporting materials.",
    countKey: "attachments",
  },
];
